



















































































































































import { mapActions, mapGetters } from 'vuex';
// utils helpers
import mixins from '@/utils/mixins';

// mixins
import componentState from '@/mixins/state';

import { ALForm } from '@/common/component.types';

import {
  IQuotePaymentTermTemplate,
  IQuotePaymentTermTemplateData,
  QuotePaymentTermTemplate,
  QuotePaymentTermTemplateFieldError,
  QuoteTemplateTranslationLanguage
} from '@/models';

import { ErrorManager } from '@/models/error';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import QuillPDFEditor from '@/components/common/QuillPDFEditor.vue';
import QuoteTemplateNoteField from '../common/QuoteTemplateNoteField.vue';
import QuotePaymentTermCodePicker from './QuotePaymentTermCodePicker.vue';
import { diffDeep } from '@/utils/helpers';
import { AxiosError } from 'axios';

const baseMixins = mixins(componentState);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'QuotePaymentTermFormDialog',

    components: {
      ErrorAlert,
      ALDialog,
      QuillPDFEditor,
      QuoteTemplateNoteField,
      QuotePaymentTermCodePicker
    },

    data: () => ({
      valid: false,
      dialog: false,
      createForm: false,

      paymentTermTemplate: new QuotePaymentTermTemplate(),
      paymentTermTemplateTranslation: new QuotePaymentTermTemplate(),

      titleRules: [
        (v: any) => !!v || 'Title is required',
        (v: any) =>
          (v && v.length <= 128) || 'Title must be at most 128 characters'
      ],
      requiredRules: [(v: string) => !!v || 'This field is required'],

      loading: false,

      errorMessage: '',
      detailErrorMessage: '',
      fieldError: new QuotePaymentTermTemplateFieldError(),

      currentLanguage: QuoteTemplateTranslationLanguage.en,
      QuoteTemplateTranslationLanguage: QuoteTemplateTranslationLanguage,

      languagesList: [
        {
          text: 'English',
          value: QuoteTemplateTranslationLanguage.en
        },
        {
          text: 'Spanish',
          value: QuoteTemplateTranslationLanguage.es
        },
        {
          text: 'Russian',
          value: QuoteTemplateTranslationLanguage.ru
        }
      ]
    }),

    computed: {
      ...mapGetters({
        getQuotePaymentTermTemplateById:
          'quoteTemplate/getQuotePaymentTermTemplateById'
      })
    },

    methods: {
      ...mapActions({
        updateQuotePaymentTermTemplate:
          'quoteTemplate/updateQuotePaymentTermTemplate',
        createQuotePaymentTermTemplate:
          'quoteTemplate/createQuotePaymentTermTemplate',

        fetchQuotePaymentTermTemplateTranslations:
          'quoteTemplate/fetchQuotePaymentTermTemplateTranslations',
        createQuotePaymentTermTemplateTranslation:
          'quoteTemplate/createQuotePaymentTermTemplateTranslation',
        updateQuotePaymentTermTemplateTranslation:
          'quoteTemplate/updateQuotePaymentTermTemplateTranslation'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';
        this.fieldError.clearErrors();
      },

      open(
        paymentTermTemplate: IQuotePaymentTermTemplate,
        createForm: boolean
      ) {
        try {
          this.clearAllErrors();
          this.currentLanguage = QuoteTemplateTranslationLanguage.en;

          this.paymentTermTemplate = new QuotePaymentTermTemplate(
            paymentTermTemplate
          );
          this.paymentTermTemplateTranslation = new QuotePaymentTermTemplate();

          this.createForm = createForm;
          this.dialog = true;
          this.setComponentState('loaded');
          this.resetFormValidation();
        } catch (err) {
          this.setComponentState('error', err);
        }
      },

      close() {
        this.dialog = false;
        this.$nextTick(() => {
          this.createForm = false;
        });
      },

      async getQuoteTemplateTranslation(
        language: QuoteTemplateTranslationLanguage
      ) {
        this.clearAllErrors();
        try {
          this.paymentTermTemplateTranslation = new QuotePaymentTermTemplate();
          if (!this.paymentTermTemplate.id) {
            return;
          }
          await this.fetchQuotePaymentTermTemplateTranslations(
            this.paymentTermTemplate.id
          ).then((templates: IQuotePaymentTermTemplateData[]) => {
            const paymentTermTemplate = templates.find(template => {
              return template.language === language;
            });
            if (paymentTermTemplate) {
              this.paymentTermTemplateTranslation = new QuotePaymentTermTemplate(
                paymentTermTemplate
              );
            }
          });
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (
              QuotePaymentTermTemplateFieldError.isQuotePaymentTermTemplateFieldError(
                error.response.data
              )
            ) {
              this.fieldError = new QuotePaymentTermTemplateFieldError(
                error.response.data
              );
            } else if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }

          this.errorMessage = ErrorManager.extractApiError(error);
        } finally {
          this.loading = false;
        }
      },

      async save() {
        if (this.validateForm()) {
          this.clearAllErrors();
          this.loading = true;
          try {
            this.paymentTermTemplateTranslation.language = this.currentLanguage;
            switch (this.currentLanguage) {
              case QuoteTemplateTranslationLanguage.es:
              case QuoteTemplateTranslationLanguage.ru:
                await this.fetchQuotePaymentTermTemplateTranslations(
                  this.paymentTermTemplate.id
                ).then((templates: IQuotePaymentTermTemplateData[]) => {
                  const paymentTermTemplate = templates.find(template => {
                    return (
                      template.language ===
                      this.paymentTermTemplateTranslation.language
                    );
                  });

                  if (paymentTermTemplate) {
                    this.updateQuotePaymentTermTemplateTranslation({
                      templateId: this.paymentTermTemplate.id,
                      translation: this.paymentTermTemplateTranslation
                    }).then((template: IQuotePaymentTermTemplateData) => {
                      this.paymentTermTemplateTranslation = new QuotePaymentTermTemplate(
                        template
                      );
                      this.close();
                    });
                  } else {
                    this.createQuotePaymentTermTemplateTranslation({
                      templateId: this.paymentTermTemplate.id,
                      translation: this.paymentTermTemplateTranslation
                    }).then((template: IQuotePaymentTermTemplateData) => {
                      this.paymentTermTemplateTranslation = new QuotePaymentTermTemplate(
                        template
                      );
                      this.close();
                    });
                  }
                });

                break;
              case QuoteTemplateTranslationLanguage.en:
              default:
                if (this.createForm) {
                  await this.createQuotePaymentTermTemplate(
                    this.paymentTermTemplate
                  );
                } else {
                  const paymentTermTemplateDelta = diffDeep(
                    this.paymentTermTemplate,
                    this.getQuotePaymentTermTemplateById(
                      this.paymentTermTemplate.id
                    ),
                    true
                  );

                  await this.updateQuotePaymentTermTemplate(
                    paymentTermTemplateDelta
                  );
                }

                this.close();
            }
          } catch (err) {
            const error = err as AxiosError;
            if (error.response) {
              if (
                QuotePaymentTermTemplateFieldError.isQuotePaymentTermTemplateFieldError(
                  error.response.data
                )
              ) {
                this.fieldError = new QuotePaymentTermTemplateFieldError(
                  error.response.data
                );
              } else if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }

            this.errorMessage = ErrorManager.extractApiError(error);
          } finally {
            this.loading = false;
          }
        }
      }
    }
  });
